<template>
  <v-alert
    v-if="options?.dirty"
    class="mt-4"
    density="compact"
    icon="mdi-sync"
    type="info"
    variant="tonal"
  >
    Les options ont été modifiées et seront envoyées au bouton lors de sa prochaine synchronisation dans quelques minutes.
  </v-alert>

  <v-row class="mt-4 pb-2">
    <v-col cols="12" md="6">
      <v-card>
        <v-card-title>
          <v-icon class="mr-1" color="primary" size="small">mdi-cog</v-icon>
          Options générales
        </v-card-title>
        <v-card-text>
          <v-progress-circular v-if="loadingOptions" color="primary" indeterminate />
          <template v-else-if="options">
            <div class="d-flex align-center ga-4">
              <span class="text-body-2 text-no-wrap">Volume ({{ options.volume_percent }}%)</span>
              <v-slider
                v-model="options.volume_percent"
                class="flex-grow-1"
                hide-details
                :max="100"
                :min="1"
                :step="1"
                thumb-label
                @end="saveOptions"
              />
            </div>
            <v-divider class="my-3" />
            <div class="d-flex align-center justify-space-between">
              <span class="text-body-2">Annoncer l'heure lors de l'appui</span>
              <v-switch
                v-model="options.time_seq_enabled"
                color="primary"
                density="compact"
                hide-details
                @update:model-value="saveOptions"
              />
            </div>
          </template>
        </v-card-text>
      </v-card>
    </v-col>

    <v-col cols="12" md="6">
      <v-card>
        <v-card-title>
          <v-icon class="mr-1" color="primary" size="small">mdi-moon-waning-crescent</v-icon>
          Mode silencieux
        </v-card-title>
        <v-card-text>
          <p class="text-medium-emphasis text-body-2 mb-3">
            Pendant ces créneaux, le bouton ne sonnera pas et ne s'allumera pas à la réception d'un message.
            La notification sera envoyée à la fin du créneau silencieux.
          </p>
          <v-progress-circular v-if="loadingOptions" color="primary" indeterminate />
          <template v-else-if="options">
            <p v-if="options.silent_mode.length === 0" class="text-medium-emphasis mb-3">
              Aucun créneau configuré. Le bouton jouera les messages à toute heure.
            </p>
            <div
              v-for="(slot, index) in options.silent_mode"
              :key="index"
              class="mb-3"
            >
              <div class="d-flex align-center ga-3 flex-wrap">
                <span class="text-body-2 text-no-wrap">De</span>
                <v-text-field
                  density="compact"
                  hide-details
                  :model-value="formatTime(slot.start_hour, slot.start_minute)"
                  style="max-width: 120px;"
                  type="time"
                  variant="outlined"
                  @update:model-value="(val: unknown) => updateSlot(slot, 'start', val)"
                />
                <span class="text-body-2 text-no-wrap">à</span>
                <v-text-field
                  density="compact"
                  hide-details
                  :model-value="formatTime(slot.end_hour, slot.end_minute)"
                  style="max-width: 120px;"
                  type="time"
                  variant="outlined"
                  @update:model-value="(val: unknown) => updateSlot(slot, 'end', val)"
                />
                <v-btn
                  color="error"
                  icon="mdi-delete"
                  size="small"
                  variant="text"
                  @click="removeSlot(index)"
                />
              </div>
              <v-divider v-if="index < options.silent_mode.length - 1" class="mt-3" />
            </div>
            <div class="d-flex ga-2 mt-2">
              <v-btn
                color="primary"
                prepend-icon="mdi-plus"
                size="small"
                variant="tonal"
                @click="addSlot"
              >
                Ajouter un créneau
              </v-btn>
              <v-btn
                v-if="silentModeDirty"
                color="primary"
                :loading="savingSilentMode"
                prepend-icon="mdi-content-save"
                size="small"
                @click="saveSilentMode"
              >
                Enregistrer
              </v-btn>
            </div>
          </template>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>

  <v-row class="mt-4 pb-2">

    <v-col cols="12" md="6">
      <v-card>
        <v-card-title>
          <v-icon class="mr-1" color="red" size="small">mdi-wifi-cog</v-icon>
          Configurer un nouveau réseau wifi
        </v-card-title>
        <v-card-text>
          <p class="mb-3">
            Le bouton change de lieu, ou vous avez changé votre mot de passe Wi-Fi ?
            Pas de panique, vous pouvez reconfigurer le réseau Wi-Fi sans réinitialiser le bouton.
          </p>
          <p class="mb-3">
            Gardez le bouton à portée de main. Assurez-vous qu'il n'est pas déjà connecté à un réseau Wi-Fi connu.
          </p>
          <v-btn color="primary" @click="openWizard">
            Démarrer la configuration
          </v-btn>
        </v-card-text>
      </v-card>
    </v-col>

    <v-col cols="12" md="6">
      <v-card>
        <v-card-title>
          <v-icon class="mr-1" color="error" size="small">mdi-delete</v-icon>
          Supprimer le bouton de mon compte
        </v-card-title>
        <v-card-text>
          <p class="mb-3">
            Cette action désassociera définitivement le bouton de votre compte.
          </p>
          <v-btn color="error" prepend-icon="mdi-delete" @click="showResetDialog = true">
            Supprimer
          </v-btn>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>

  <v-dialog v-model="showResetDialog" max-width="500" persistent>
    <v-card>
      <v-card-title class="text-h6">
        <v-icon class="mr-1" color="error">mdi-alert</v-icon>
        Confirmer la suppression
      </v-card-title>
      <v-card-text>
        <p class="mb-3">
          Êtes-vous sûr de vouloir supprimer le bouton <strong>{{ device.name }}</strong> de votre compte ?
          Cette action est irréversible.
        </p>
        <v-alert
          class="mb-3"
          density="compact"
          icon="mdi-information-outline"
          type="warning"
          variant="tonal"
        >
          Pensez également à réinitialiser physiquement le bouton en restant appuyé sur le bouton situé à l'arrière pendant plus de 5 secondes.
          Cela effacera les messages stockés et remettra le bouton à zéro. Il sera alors prêt à être associé à un autre utilisateur.
        </v-alert>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn :disabled="resetting" variant="text" @click="showResetDialog = false">
          Annuler
        </v-btn>
        <v-btn
          color="error"
          :loading="resetting"
          prepend-icon="mdi-delete"
          variant="flat"
          @click="resetDevice"
        >
          Confirmer la suppression
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <v-dialog v-model="isWizardOpen" max-width="500" persistent>
    <TheBluetoothWizard
      :is-reconfigure="true"
      @close="closeWizard"
      @success="handleWizardSuccess"
    />
  </v-dialog>

</template>

<script setup lang="ts">
  import type { DeviceOptions } from '@/model/device-options.ts'
  import type { Device } from '@/model/device.ts'
  import { onMounted, ref } from 'vue'
  import { useRouter } from 'vue-router'
  import { deviceRepository } from '@/api/repository/device.repository.ts'
  import TheBluetoothWizard from '@/components/TheBluetoothWizard.vue'
  import { SilentModeSlot } from '@/model/device-options.ts'
  import { ToastType, useToastStore } from '@/stores/toast.store.ts'
  import { notifyApiError } from '@/utils/error.utils.ts'

  const props = defineProps<{
    device: Device
  }>()

  const emit = defineEmits<{
    deleted: []
  }>()

  const router = useRouter()
  const toastStore = useToastStore()

  const options = ref<DeviceOptions | null>(null)
  const loadingOptions = ref(true)
  const showResetDialog = ref(false)
  const resetting = ref(false)
  const silentModeDirty = ref(false)
  const savingSilentMode = ref(false)

  async function resetDevice () {
    resetting.value = true
    try {
      await deviceRepository.reset(props.device.id)
      showResetDialog.value = false
      toastStore.notify('Le bouton a été supprimé de votre compte.', ToastType.Success)
      emit('deleted')
      router.replace({ name: 'devices' })
    } catch (error) {
      notifyApiError('Erreur lors de la suppression du bouton', error)
    } finally {
      resetting.value = false
    }
  }

  async function loadOptions () {
    loadingOptions.value = true
    try {
      options.value = await deviceRepository.getOptions(props.device.id)
    } catch (error) {
      notifyApiError('Impossible de charger les options du bouton', error)
    } finally {
      loadingOptions.value = false
    }
  }

  function formatTime (hour: number, minute: number): string {
    return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
  }

  // Le type="time" du HTML retourne toujours "HH:MM" en 24h, quel que soit le locale du navigateur.
  function updateSlot (slot: SilentModeSlot, field: 'start' | 'end', value: unknown) {
    const str = String(value ?? '')
    if (!str.includes(':')) return
    const parts = str.split(':').map(Number)
    const hour = parts[0] ?? 0
    const minute = parts[1] ?? 0
    if (field === 'start') {
      slot.start_hour = hour
      slot.start_minute = minute
    } else {
      slot.end_hour = hour
      slot.end_minute = minute
    }
    silentModeDirty.value = true
  }

  function addSlot () {
    if (!options.value) return
    options.value.silent_mode.push(new SilentModeSlot(22, 0, 7, 0))
    silentModeDirty.value = true
  }

  function removeSlot (index: number) {
    if (!options.value) return
    options.value.silent_mode.splice(index, 1)
    silentModeDirty.value = true
  }

  async function saveSilentMode () {
    if (!options.value) return
    savingSilentMode.value = true
    try {
      options.value = await deviceRepository.updateOptions(props.device.id, options.value)
      silentModeDirty.value = false
      toastStore.notify('Paramètres enregistrés.', ToastType.Success)
    } catch (error) {
      notifyApiError('Erreur lors de l\'enregistrement du mode silencieux', error)
    } finally {
      savingSilentMode.value = false
    }
  }

  const isWizardOpen = ref(false)

  function openWizard () {
    isWizardOpen.value = true
  }

  function closeWizard () {
    isWizardOpen.value = false
  }

  function handleWizardSuccess () {
    closeWizard()
    toastStore.notify('Les paramètres Wi-Fi ont été envoyés au bouton.', ToastType.Success)
  }

  async function saveOptions () {
    if (!options.value) return
    try {
      options.value = await deviceRepository.updateOptions(props.device.id, options.value)
      toastStore.notify('Paramètres enregistrés.', ToastType.Success)
    } catch (error) {
      notifyApiError('Erreur lors de l\'enregistrement des paramètres', error)
    }
  }

  onMounted(() => {
    loadOptions()
  })

  defineExpose({ loadOptions })
</script>

<style scoped>
:deep(.v-card-title) {
  white-space: normal !important;
  overflow: visible !important;
  text-overflow: unset !important;
}
</style>
